import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, RefreshCw, Check, X, AlertCircle, AlertTriangle, CreditCard, HandCoins, Plus, ScrollText,
} from 'lucide-react';
import { PRODUCTOS } from '../../constants/productos';
import { fmtMoney } from '../../utils/money';
import { describirProductos, horaMx } from '../../components/SolicitudesCreditoPunto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function deudaTxt(saldo) {
  if (saldo == null) return '';
  if (saldo < 0) return `Debe ${fmtMoney(-saldo)}`;
  if (saldo > 0) return `Saldo A FAVOR ${fmtMoney(saldo)}`;
  return 'Sin deuda';
}

function TarjetaSolicitud({ s, onCambio }) {
  const navigate = useNavigate();
  const [modo, setModo] = useState(null); // 'rechazar' | 'forzar'
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const monto = s.tipo === 'venta_credito' ? s.total : s.monto;

  const aprobar = async (forzar = false) => {
    setEnviando(true);
    setError('');
    try {
      await axios.put(`${API}/admin/solicitudes-credito/${s.id}/aprobar`, { forzar });
      onCambio();
    } catch (e) {
      if (e.response?.status === 409) {
        setAviso(e.response.data.detail);
        setModo('forzar');
      } else {
        setError(e.response?.data?.detail || 'No se pudo aprobar');
      }
    } finally {
      setEnviando(false);
    }
  };

  const rechazar = async () => {
    setEnviando(true);
    setError('');
    try {
      await axios.put(`${API}/admin/solicitudes-credito/${s.id}/rechazar`, { motivo: motivo.trim() });
      onCambio();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo rechazar');
    } finally {
      setEnviando(false);
    }
  };

  const Icon = s.tipo === 'venta_credito' ? CreditCard : HandCoins;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-2" data-testid={`solicitud-${s.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.tipo === 'venta_credito' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2">
            <p className="font-bold text-zinc-900">{s.tipo_label}</p>
            <p className="text-sm text-zinc-500">{s.punto_nombre}</p>
          </div>
          <p className="text-sm text-zinc-800">{s.cliente_nombre}
            <span className={`ml-2 text-xs ${s.saldo_cliente > 0 ? 'text-red-600 font-semibold' : 'text-zinc-400'}`}>
              {deudaTxt(s.saldo_cliente)}
            </span>
          </p>
          {s.tipo === 'venta_credito' && <p className="text-xs text-zinc-500">{describirProductos(s.productos)}</p>}
          {s.nota && <p className="text-xs text-zinc-500 italic">“{s.nota}”</p>}
          <p className="text-[11px] text-zinc-400 mt-0.5">
            {s.captura_directa ? 'Captura directa' : 'Solicitó'} {s.solicitado_por_nombre} · turno {s.fecha} · {horaMx(s.created_at)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono font-bold text-lg text-zinc-900">{fmtMoney(monto)}</p>
          <button
            onClick={() => navigate(`/admin/bitacora?punto=${s.punto_venta_id}&fecha=${s.fecha}`)}
            className="mt-1 text-zinc-400 hover:text-zinc-600" title="Ver bitácora del turno"
          >
            <ScrollText className="w-4 h-4" />
          </button>
        </div>
      </div>

      {s.status !== 'pendiente' && (
        <p className={`text-xs ${s.status === 'aprobada' ? 'text-emerald-700' : 'text-red-700'}`}>
          {s.status === 'aprobada' ? 'Aprobada' : 'Rechazada'} por {s.resuelto_por_nombre} · {horaMx(s.resuelto_at)}
          {s.forzada && ' · aprobada aunque excedía la deuda'}
          {s.motivo_rechazo && ` · Motivo: ${s.motivo_rechazo}`}
        </p>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {s.status === 'pendiente' && modo === null && (
        <div className="flex gap-2 pt-1">
          <button onClick={() => setModo('rechazar')} disabled={enviando}
            className="flex-1 h-10 border border-red-300 text-red-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
            <X className="w-4 h-4" /> Rechazar
          </button>
          <button onClick={() => aprobar(false)} disabled={enviando}
            className="flex-1 h-10 bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid="btn-aprobar">
            {enviando ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <><Check className="w-4 h-4" /> Aprobar</>}
          </button>
        </div>
      )}

      {modo === 'forzar' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
          <p className="text-sm text-amber-800 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{aviso}</p>
          <div className="flex gap-2">
            <button onClick={() => setModo(null)} className="flex-1 h-9 border border-zinc-300 rounded-lg text-sm bg-white">Volver</button>
            <button onClick={() => aprobar(true)} disabled={enviando}
              className="flex-1 h-9 bg-amber-600 text-white rounded-lg text-sm font-semibold">Aprobar de todos modos</button>
          </div>
        </div>
      )}

      {modo === 'rechazar' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
            placeholder="Motivo del rechazo (la vendedora lo verá en su corte)"
            className="w-full p-2 text-sm border border-red-200 rounded-lg bg-white" data-testid="input-motivo" />
          <p className="text-[11px] text-red-700">
            {s.tipo === 'venta_credito'
              ? 'El pollo entregado quedará como faltante de la vendedora en su corte.'
              : 'El dinero recibido quedará como sobrante en su corte y deberá entregarlo.'}
          </p>
          <div className="flex gap-2">
            <button onClick={() => { setModo(null); setMotivo(''); }} className="flex-1 h-9 border border-zinc-300 rounded-lg text-sm bg-white">Cancelar</button>
            <button onClick={rechazar} disabled={enviando || motivo.trim().length < 3}
              className="flex-1 h-9 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              data-testid="btn-confirmar-rechazo">Rechazar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CapturaDirecta({ onListo }) {
  const [puntos, setPuntos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [punto, setPunto] = useState('');
  const [tipo, setTipo] = useState('venta_credito');
  const [cliente, setCliente] = useState('');
  const [precios, setPrecios] = useState({});
  const [cantidades, setCantidades] = useState({});
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/puntos-venta`).then(({ data }) => setPuntos(data.sort((a, b) => a.name.localeCompare(b.name, 'es')))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!punto) return;
    axios.get(`${API}/clientes/${punto}`).then(({ data }) => setClientes(data.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')))).catch(() => {});
    axios.get(`${API}/precios/${punto}`).then(({ data }) => setPrecios(data.precios || {})).catch(() => setPrecios({}));
  }, [punto]);

  const total = useMemo(() => Object.entries(cantidades)
    .reduce((s, [k, q]) => s + (parseFloat(q) || 0) * (precios[k] || 0), 0), [cantidades, precios]);

  const enviar = async (forzar = false) => {
    setEnviando(true);
    setError('');
    try {
      const productos = Object.fromEntries(Object.entries(cantidades).filter(([, q]) => parseFloat(q) > 0).map(([k, q]) => [k, parseFloat(q)]));
      await axios.post(`${API}/admin/credito-directo`, {
        punto_venta_id: punto, tipo, cliente_id: cliente, nota: nota || null, forzar,
        ...(tipo === 'venta_credito' ? { productos } : { monto: parseFloat(monto) }),
      });
      onListo();
    } catch (e) {
      if (e.response?.status === 409) setAviso(e.response.data.detail);
      else setError(e.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setEnviando(false);
    }
  };

  const listo = punto && cliente && (tipo === 'venta_credito' ? total > 0 : parseFloat(monto) > 0);
  return (
    <div className="bg-white border-2 border-zinc-900 rounded-xl p-4 space-y-3">
      <p className="font-bold text-zinc-900">Captura directa</p>
      <p className="text-xs text-zinc-500">Para cuando la vendedora no puede enviar la solicitud (p. ej. sin señal). Queda registrada a tu nombre en el turno activo del punto.</p>
      <div className="grid grid-cols-2 gap-2">
        {[['venta_credito', 'Venta a crédito'], ['abono', 'Abono']].map(([v, l]) => (
          <button key={v} onClick={() => setTipo(v)}
            className={`h-10 rounded-lg text-sm font-medium border ${tipo === v ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300'}`}>{l}</button>
        ))}
      </div>
      <select value={punto} onChange={e => { setPunto(e.target.value); setCliente(''); setCantidades({}); }}
        className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm bg-white">
        <option value="">Punto de venta…</option>
        {puntos.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={cliente} onChange={e => setCliente(e.target.value)} disabled={!punto}
        className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm bg-white disabled:opacity-50">
        <option value="">Cliente…</option>
        {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre} {c.saldo < 0 ? `(debe ${fmtMoney(-c.saldo)})` : c.saldo > 0 ? `(a favor ${fmtMoney(c.saldo)})` : ''}</option>)}
      </select>
      {tipo === 'venta_credito' ? (
        <div className="grid grid-cols-2 gap-2">
          {PRODUCTOS.filter(p => p.clave !== '8').map(p => (
            <label key={p.clave} className="flex items-center gap-2 text-sm">
              <input type="number" min="0" step={p.allowHalf ? '0.5' : '1'} inputMode="decimal"
                value={cantidades[p.clave] ?? ''} onChange={e => setCantidades(prev => ({ ...prev, [p.clave]: e.target.value }))}
                className="w-16 h-9 px-2 border border-zinc-300 rounded-lg text-right font-mono" />
              <span className="truncate">{p.nombre}</span>
              <span className="ml-auto text-xs text-zinc-400">{precios[p.clave] ? fmtMoney(precios[p.clave]) : 'sin precio'}</span>
            </label>
          ))}
          <p className="col-span-2 text-right font-mono font-bold">Total {fmtMoney(total)}</p>
        </div>
      ) : (
        <input type="number" min="0" step="0.01" inputMode="decimal" value={monto} onChange={e => setMonto(e.target.value)}
          placeholder="Monto del abono" className="w-full h-12 px-3 border border-zinc-300 rounded-lg text-lg font-mono" />
      )}
      <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota (opcional)"
        className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
      {error && <p className="text-sm text-red-600">{error}</p>}
      {aviso ? (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
          <p className="text-sm text-amber-800">{aviso}</p>
          <div className="flex gap-2">
            <button onClick={() => setAviso('')} className="flex-1 h-9 border border-zinc-300 rounded-lg text-sm bg-white">Volver</button>
            <button onClick={() => enviar(true)} className="flex-1 h-9 bg-amber-600 text-white rounded-lg text-sm font-semibold">Registrar de todos modos</button>
          </div>
        </div>
      ) : (
        <button onClick={() => enviar(false)} disabled={!listo || enviando}
          className="w-full h-11 bg-zinc-900 text-white rounded-lg font-semibold disabled:opacity-50">
          Registrar {tipo === 'venta_credito' ? 'venta a crédito' : 'abono'}
        </button>
      )}
    </div>
  );
}

export function AdminAutorizaciones() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pendiente');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captura, setCaptura] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/solicitudes-credito`, { params: { status: tab, dias: 7 } });
      setItems(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (tab !== 'pendiente') return undefined;
    const id = setInterval(cargar, 30000);
    return () => clearInterval(id);
  }, [tab, cargar]);

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900">Autorizaciones</h1>
            <p className="text-xs text-zinc-500">Ventas a crédito y abonos solicitados por los puntos</p>
          </div>
          <button onClick={cargar} disabled={loading} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-500">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 pb-24 max-w-3xl mx-auto">
        <div className="flex gap-2">
          {[['pendiente', 'Pendientes'], ['resueltas', 'Resueltas (7 días)']].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`px-4 h-10 rounded-lg text-sm font-medium border ${tab === v ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-300'}`}>
              {l}{v === 'pendiente' && tab === 'pendiente' && items.length > 0 ? ` · ${items.length}` : ''}
            </button>
          ))}
          <button onClick={() => setCaptura(c => !c)}
            className="ml-auto px-3 h-10 rounded-lg text-sm font-medium border border-zinc-300 bg-white flex items-center gap-1">
            <Plus className="w-4 h-4" /> Captura directa
          </button>
        </div>

        {captura && <CapturaDirecta onListo={() => { setCaptura(false); setTab('resueltas'); cargar(); }} />}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className="text-center text-sm text-zinc-400 py-16">
            {tab === 'pendiente' ? 'No hay solicitudes pendientes' : 'Sin solicitudes resueltas en los últimos 7 días'}
          </p>
        )}

        <div className="space-y-3">
          {items.map(s => <TarjetaSolicitud key={s.id} s={s} onCambio={cargar} />)}
        </div>
      </div>
    </div>
  );
}
